import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { user, loading, profile } = useAuth();
  const location = useLocation();
  const isOnboardingPath = location.pathname === '/onboarding';

  if (loading) {
    // You can show a loading spinner here
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-roundone-purple"></div>
      </div>
    );
  }

  if (!user) {
    // Redirect to home if not authenticated
    return <Navigate to="/" replace />;
  }

  // Check if user needs onboarding (has no organization name)
  // Only redirect if user is not already on the onboarding page
  const hasOrganizationName = profile?.organizations?.name && profile.organizations.name.trim() !== '';
  const needsOnboarding = !hasOrganizationName && !isOnboardingPath;
  
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;

export const PublicGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    // Show loading state
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-roundone-purple border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    // Redirect authenticated users to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
